import type { PaletteEntry } from "./types";

// ── Search ranking for the command palette ────────────────────────────────
// Every word typed must match something (the label, a keyword, the section or
// the page's one-line description), so "risk supplier" narrows rather than
// widens. Each word scores by where it matched — a whole label word beats the
// start of one, which beats a keyword, which beats the description — and a
// word one or two letters off still finds its page ("vulnerabilty",
// "insurence"), at the lowest score, so a correct spelling elsewhere wins.

const SCORE = {
  labelExact: 120,
  labelWord: 60,
  labelPrefix: 45,
  labelInfix: 30,
  keywordWord: 35,
  keywordPrefix: 28,
  keywordInfix: 18,
  hint: 10,
  description: 8,
  fuzzy: 6,
} as const;

function normalise(s: string): string {
  return s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9.&+\s-]/g, " ");
}

function words(s: string): string[] {
  return normalise(s).split(/[\s\-/]+/).filter(Boolean);
}

/** Optimal-string-alignment distance, giving up once it passes `max`. */
function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev2 = new Array<number>(b.length + 1).fill(0);
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
      cur.push(v);
      rowMin = Math.min(rowMin, v);
    }
    if (rowMin > max) return max + 1;
    for (let j = 0; j <= b.length; j++) prev2[j] = prev[j];
    prev = cur;
  }
  return prev[b.length];
}

/**
 * How many typos a word of this length may carry: none under four letters, and
 * none in anything with a digit — A.8.12 is not one letter away from A.5.12, it
 * is a different control.
 */
function typoAllowance(term: string): number {
  if (term.length < 4 || /\d/.test(term)) return 0;
  return term.length < 8 ? 1 : 2;
}

interface Haystack {
  label: string;
  labelWords: string[];
  keywords: string[];
  keywordWords: string[];
  hint: string;
  description: string;
  allWords: string[];
}

function haystackOf(entry: PaletteEntry): Haystack {
  const label = normalise(entry.label);
  const keywords = (entry.keywords ?? []).map(normalise);
  const hint = normalise(entry.hint ?? "");
  const description = normalise(entry.description ?? "");
  const labelWords = words(entry.label);
  const keywordWords = keywords.flatMap((k) => words(k));
  return {
    label, labelWords, keywords, keywordWords, hint, description,
    allWords: [...labelWords, ...keywordWords, ...words(entry.hint ?? ""), ...words(entry.description ?? "")],
  };
}

function termScore(term: string, h: Haystack): number {
  if (h.labelWords.includes(term)) return SCORE.labelWord;
  if (h.labelWords.some((w) => w.startsWith(term))) return SCORE.labelPrefix;
  if (h.label.includes(term)) return SCORE.labelInfix;
  if (h.keywordWords.includes(term) || h.keywords.includes(term)) return SCORE.keywordWord;
  if (h.keywordWords.some((w) => w.startsWith(term))) return SCORE.keywordPrefix;
  if (h.keywords.some((k) => k.includes(term))) return SCORE.keywordInfix;
  if (h.hint.includes(term)) return SCORE.hint;
  if (h.description.includes(term)) return SCORE.description;
  const allowance = typoAllowance(term);
  if (allowance > 0 && h.allWords.some((w) => w.length >= 3 && editDistance(term, w, allowance) <= allowance)) return SCORE.fuzzy;
  return 0;
}

/** Score of one entry for a query; 0 means it does not match. */
export function scoreEntry(entry: PaletteEntry, query: string): number {
  const q = normalise(query).trim();
  if (!q) return 1;
  const h = haystackOf(entry);
  // A phrase typed whole ("risk register", "what applies") scores above its words.
  let total = h.label === q ? SCORE.labelExact : h.label.startsWith(q) ? SCORE.labelPrefix * 2 : 0;
  if (total === 0 && h.keywords.includes(q)) total = SCORE.keywordWord * 2;
  for (const term of words(q)) {
    const s = termScore(term, h);
    if (s === 0) return 0;
    total += s;
  }
  // A shorter label is the closer match when the words score the same.
  return total - Math.min(entry.label.length, 40) / 100;
}

/** The entries that match, best first; ties keep the order they were given in. */
export function rankEntries(entries: PaletteEntry[], query: string, limit: number): PaletteEntry[] {
  return entries
    .map((entry, index) => ({ entry, index, score: scoreEntry(entry, query) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, limit)
    .map((r) => r.entry);
}
